import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { CheckSquare, ChevronLeft, LayoutGrid, List, Sparkles, UserCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

export const TABS = [
  { to: "/", label: "Home", icon: LayoutGrid, testID: "tab-home" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testID: "tab-tasks" },
  { to: "/todos", label: "To-Do", icon: List, testID: "tab-todos" },
  { to: "/ai", label: "AI", icon: Sparkles, testID: "tab-ai" },
  { to: "/profile", label: "Profile", icon: UserCircle, testID: "tab-profile" },
];

export function useBack(fallback = "/") {
  const navigate = useNavigate();
  const location = useLocation();
  return () => { if (location.key && location.key !== "default") navigate(-1); else navigate(fallback, { replace: true }); };
}

/** Tab screens: bottom tab bar on mobile, left sidebar on desktop. */
export function TabsLayout({ children }) {
  const { activeWorkspace } = useAuth();
  return (
    <div className="min-h-screen bg-a-bg md:flex">
      <aside className="sticky top-0 hidden h-screen w-[240px] shrink-0 flex-col border-r border-a-border bg-a-surface px-4 py-6 md:flex">
        <div className="mb-8 flex items-center gap-2 px-2">
          <span className="block h-[27px] w-9 overflow-hidden"><img src="/branding/assignoo-tick.png" alt="Assignoo" className="w-9 max-w-none" /></span>
          <span className="text-xl font-extrabold tracking-tight text-a-text">assignoo</span>
        </div>
        <nav className="flex flex-col gap-1">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.to === "/"}
              data-testid={`side-${t.testID}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors ${isActive ? "bg-a-dim text-a-primary" : "text-a-muted hover:bg-a-text/5 hover:text-a-text"}`}
            >
              <t.icon size={20} /> {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto rounded-xl border border-a-border bg-a-bg px-3 py-2.5">
          <p className="ov text-[10px] font-bold uppercase tracking-[1.4px] text-a-muted">Workspace</p>
          <p className="truncate text-sm font-extrabold text-a-text">{activeWorkspace?.name || "—"}</p>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <div className="mx-auto w-full max-w-3xl pb-[96px] md:pb-10">{children}</div>
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-50 flex h-[64px] border-t border-a-border bg-a-surface/95 pb-[env(safe-area-inset-bottom)] pt-2 backdrop-blur md:hidden">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === "/"}
            data-testid={t.testID}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 text-[11px] font-bold ${isActive ? "text-a-primary" : "text-a-muted"}`}
          >
            <t.icon size={24} strokeWidth={1.8} />
            <span>{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** Stack screens: header with back chevron + centered content column. */
export function StackPage({ title, subtitle, right, children, backIcon, onBack, testID, contentClassName = "p-4 space-y-3.5" }) {
  const back = useBack();
  const Icon = backIcon || ChevronLeft;
  return (
    <div className="min-h-screen bg-a-bg" data-testid={testID}>
      <header className="sticky top-0 z-40 border-b border-a-border bg-a-bg/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-2.5 px-3 py-2.5">
          <button onClick={onBack || back} className="rounded-lg p-0.5 text-a-text hover:bg-a-text/5" data-testid="header-back">
            <Icon size={26} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] font-extrabold text-a-text">{title}</h1>
            {subtitle ? <p className="truncate text-xs text-a-muted">{subtitle}</p> : null}
          </div>
          {right}
        </div>
      </header>
      <div className={`page-in mx-auto max-w-2xl pb-10 ${contentClassName}`}>{children}</div>
    </div>
  );
}

export function AuthBackground({ children }) {
  return (
    <div className="auth-bg min-h-screen">
      <div className="page-in mx-auto w-full max-w-[440px] px-6 pb-8 pt-10">{children}</div>
    </div>
  );
}

export function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function WhatsAppIcon({ size = 18, color = "#25D366" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.4 9.4 0 0 1-1.44-5.02c0-5.2 4.24-9.43 9.44-9.43 2.52 0 4.89.98 6.67 2.77a9.37 9.37 0 0 1 2.76 6.67c0 5.2-4.24 9.43-9.44 9.43M20.08 3.9A11.3 11.3 0 0 0 12.04.57C5.79.57.7 5.66.7 11.91c0 2 .52 3.95 1.52 5.66L.6 23.44l6-1.57a11.3 11.3 0 0 0 5.43 1.38h.01c6.25 0 11.34-5.09 11.34-11.34 0-3.03-1.18-5.88-3.32-8.02" />
    </svg>
  );
}
